import { useQuery } from "@tanstack/react-query";
import { useOrganization } from "@clerk/clerk-react";
import { BUY_AGAIN_DEFAULT_LIMIT } from "@shared/buy-again-constants";
import { useProductsLookup } from "./use-products";
import { client, handleClientMethod } from "../requests/client";
import type { Product } from "@/types/product";

export interface BuyAgainItem {
  product: Product;
  avgIntervalDays: number;
  score: number;
  gapDays: number;
  z: number;
  gapCloseness: number;
  volume: number;
  stability: number;
}

export function useBuyAgain(limit = BUY_AGAIN_DEFAULT_LIMIT) {
  const { organization } = useOrganization();

  const {
    data: suggestions = [],
    isLoading: suggestionsLoading,
    error: suggestionsError,
  } = useQuery({
    queryKey: ["buy-again", organization?.id, limit],
    queryFn: () =>
      handleClientMethod(client.GET("/api/buy-again", { params: { query: { limit } } })),
    enabled: !!organization?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const productIds = (suggestions ?? []).map((s) => s.productId);
  const { products, isLoading: productsLoading, error: productsError } = useProductsLookup(productIds);

  const items: BuyAgainItem[] = (suggestions ?? [])
    .map((s) => {
      const product = products.find((p) => p.uid === s.productId);
      if (!product) return null;
      return {
        product,
        avgIntervalDays: s.avgIntervalDays,
        score: s.score,
        gapDays: s.gapDays,
        z: s.z,
        gapCloseness: s.gapCloseness,
        volume: s.volume,
        stability: s.stability,
      };
    })
    .filter((item): item is BuyAgainItem => item !== null);

  return {
    items,
    isLoading: suggestionsLoading || productsLoading,
    error: suggestionsError ?? productsError ?? null,
  };
}
